import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

/**
 * A rich text editor component based on TipTap (toolbar style - like MS Word)
 * Replacement for ReactQuill with snow theme
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.value=''] - The content value of the editor
 * @param {Function} props.onChange - Callback function triggered when content changes
 * @param {string} [props.placeholder='Type rich text'] - Placeholder text when editor is empty
 * @param {string} [props.minHeight='80px'] - Minimum height of the editor
 * @param {string} [props.width='100%'] - Width of the editor
 * @param {string} [props.padding='5px 5px'] - Internal padding of the editor container
 * @param {string} [props.border='1px solid #b4b7ba'] - Border style of the editor
 * @param {string} [props.borderRadius='5px'] - Border radius of the editor
 * @param {Object} [props.containerDivStyles={}] - Additional container styles
 * @param {React.Ref} ref - Forwarded ref to access the editor instance
 * @returns {JSX.Element} The rich text editor component
 */
const RichTextEditorMSWordStyle = forwardRef(
  (
    {
      value = '',
      onChange,
      placeholder = 'Type rich text',
      minHeight = '80px',
      width = '100%',
      padding = '5px 5px',
      border = '1px solid #b4b7ba',
      borderRadius = '5px',
      containerDivStyles = {},
      ...props
    },
    ref,
  ) => {
    const editorRef = useRef(null)
    const isInternalUpdate = useRef(false)

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          link: false,
          underline: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'tiptap-link',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
      ],
      content: value || '',
      onUpdate: ({ editor }) => {
        isInternalUpdate.current = true
        const html = editor.getHTML()
        // Convert empty paragraph to empty string for consistency with ReactQuill
        const cleanedHtml = html === '<p></p>' ? '' : html
        if (onChange) {
          onChange(cleanedHtml)
        }
        // Reset flag after a short delay to allow for external updates
        setTimeout(() => {
          isInternalUpdate.current = false
        }, 0)
      },
      editorProps: {
        attributes: {
          class: 'tiptap-snow-editor',
          style: `min-height: ${minHeight}; padding: ${padding};`,
        },
      },
      autofocus: false,
    })

    // Store editor ref
    useEffect(() => {
      if (editor) {
        editorRef.current = editor
      }
    }, [editor])

    // Update editor content when value prop changes (external updates only)
    useEffect(() => {
      if (editor && value !== undefined && !isInternalUpdate.current) {
        const currentContent = editor.getHTML()
        const normalizedCurrent = currentContent === '<p></p>' ? '' : currentContent
        const normalizedValue = value || ''
        if (normalizedCurrent !== normalizedValue) {
          editor.commands.setContent(normalizedValue, false)
        }
      }
    }, [editor, value])

    // Expose editor methods through useImperativeHandle for compatibility
    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
      getEditorElement: () => editor?.view?.dom,
      focus: () => {
        if (editor) {
          editor.commands.focus()
        }
      },
      blur: () => {
        if (editor) {
          editor.commands.blur()
        }
      },
      // For compatibility with old ReactQuill API
      quill: null,
      editor: editor,
    }))

    if (!editor) {
      return null
    }

    return (
      <div
        style={{
          width,
          border,
          borderRadius,
          backgroundColor: '#fff',
          overflow: 'hidden',
          ...(containerDivStyles || {}),
        }}
      >
        {/* Toolbar */}
        <div className="tiptap-snow-toolbar">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            title="Underline"
          >
            <u>U</u>
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            title="Ordered List"
          >
            1.
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt('Enter URL:')
              if (url) {
                editor.chain().focus().setLink({ href: url }).run()
              }
            }}
            className={editor.isActive('link') ? 'is-active' : ''}
            title="Link"
          >
            🔗
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'is-active' : ''}
            title="Quote"
          >
            "
          </button>
        </div>

        <EditorContent editor={editor} />

        <style dangerouslySetInnerHTML={{
          __html: `
            .tiptap-snow-toolbar {
              display: flex;
              align-items: center;
              gap: 4px;
              padding: 8px 12px;
              background: #f8fafc;
              border-bottom: 1px solid #e2e8f0;
              flex-wrap: wrap;
            }
            .tiptap-snow-toolbar button {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 4px 8px;
              border: none;
              background: transparent;
              border-radius: 4px;
              cursor: pointer;
              font-size: 13px;
              color: #64748b;
              transition: all 0.15s;
              min-width: 28px;
              height: 28px;
            }
            .tiptap-snow-toolbar button:hover {
              background: #e2e8f0;
              color: #1e293b;
            }
            .tiptap-snow-toolbar button.is-active {
              background: #6779e3;
              color: #fff;
            }
            .tiptap-snow-toolbar .toolbar-divider {
              width: 1px;
              height: 20px;
              background: #e2e8f0;
              margin: 0 4px;
            }
            .tiptap-snow-editor {
              outline: none;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #1f2937;
            }
            .tiptap-snow-editor p {
              margin: 0.5em 0;
            }
            .tiptap-snow-editor p:first-child {
              margin-top: 0;
            }
            .tiptap-snow-editor p:last-child {
              margin-bottom: 0;
            }
            .tiptap-snow-editor p.is-editor-empty:first-child::before {
              content: attr(data-placeholder);
              float: left;
              color: #adb5bd;
              pointer-events: none;
              height: 0;
            }
            .tiptap-snow-editor h1 {
              font-size: 1.8em;
              font-weight: 700;
              margin: 0.5em 0;
            }
            .tiptap-snow-editor h2 {
              font-size: 1.4em;
              font-weight: 600;
              margin: 0.5em 0;
            }
            .tiptap-snow-editor h3 {
              font-size: 1.2em;
              font-weight: 600;
              margin: 0.5em 0;
            }
            .tiptap-snow-editor .tiptap-link {
              color: #6779e3;
              text-decoration: underline;
            }
            .tiptap-snow-editor ul,
            .tiptap-snow-editor ol {
              padding-left: 1.5em;
              margin: 0.5em 0;
            }
            .tiptap-snow-editor blockquote {
              border-left: 3px solid #6779e3;
              padding-left: 1em;
              margin: 0.5em 0;
              color: #64748b;
              font-style: italic;
            }
          `
        }} />
      </div>
    )
  },
)

RichTextEditorMSWordStyle.displayName = 'RichTextEditorMSWordStyle'

export default RichTextEditorMSWordStyle
